import {ChildrenProps} from '@app/helpers/react';
import {createContext, useContext, useLayoutEffect, useState} from 'react';
import {createPortal} from 'react-dom';

import {LOXO_FLOATING_BASE, LOXO_FLOATING_PRIORITY} from './constants/floating';

interface Props extends ChildrenProps {
  alwaysOnTop?: boolean;

  useParentPriority?: boolean;
}

const context = createContext<boolean>(false);

const useFloatingPortalNode = (id: string) => {
  const [portalEl, setPortalEl] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const rootNode = document.getElementById(id);

    if (rootNode) {
      setPortalEl(rootNode);
    } else {
      const newPortalEl = document.createElement('div');
      newPortalEl.classList.add('no-link-styles');
      newPortalEl.style.position = 'fixed';
      newPortalEl.style.zIndex = '10';
      newPortalEl.style.width = '100%';
      newPortalEl.style.bottom = '0';
      newPortalEl.id = id;
      setPortalEl(newPortalEl);

      if (!document.body.contains(newPortalEl)) {
        document.body.appendChild(newPortalEl);
      }
    }
  }, [id]);

  return portalEl;
};

const FloatingPortal = ({
  children,
  alwaysOnTop = false,
  useParentPriority = false,
}: Props) => {
  const basePortalNode = useFloatingPortalNode(LOXO_FLOATING_BASE);
  const priorityPortalNode = useFloatingPortalNode(LOXO_FLOATING_PRIORITY);

  const parentPriority = useContext(context);
  const usePriority = useParentPriority ? parentPriority : alwaysOnTop;

  if (!basePortalNode || !priorityPortalNode) {
    return null;
  }

  return (
    <context.Provider value={usePriority}>
      {createPortal(
        children,
        usePriority ? priorityPortalNode : basePortalNode
      )}
    </context.Provider>
  );
};

export default FloatingPortal;
